/**
 * Registration Application Service
 * 
 * Focused service for user registration
 * Separate from authentication to maintain single responsibility
 */
import { PersonAuth } from '../../domain/aggregates/PersonAuth';
import type { IPersonAuthRepository } from '../../domain/repositories/IPersonAuthRepository';
import type { IPersonRepository } from '../../../parties/domain/repositories/IPersonRepository';

export interface RegisterCommand {
  personId: string;
  email: string;
  password: string;
}

export interface RegisterResult {
  success: boolean;
  personAuthId?: string;
  message?: string;
  error?: string;
}

export class RegistrationService {
  constructor(
    private personAuthRepo: IPersonAuthRepository,
    private personRepo: IPersonRepository,
    private eventBus: { publish: (event: any) => Promise<void> }
  ) {}

  /**
   * Register new user authentication
   */
  async register(command: RegisterCommand): Promise<RegisterResult> {
    try {
      // 1. Verify person exists
      const person = await this.personRepo.findById(command.personId);
      if (!person) {
        return {
          success: false,
          error: 'Person not found'
        };
      }

      // 2. Check email uniqueness
      if (await this.personAuthRepo.emailExists(command.email)) {
        return {
          success: false,
          error: 'Email already registered'
        };
      }

      // 3. Verify email matches person record
      if (person.getEmail() !== command.email) {
        return {
          success: false,
          error: 'Email does not match person record'
        };
      }

      // 4. Create aggregate using factory method
      const personAuth = await PersonAuth.create(
        command.personId,
        command.email,
        command.password
      );

      // 5. Save aggregate
      await this.personAuthRepo.save(personAuth);

      // 6. Publish domain events
      await this.publishEvents(personAuth);

      // 7. Return success
      return {
        success: true,
        personAuthId: personAuth.getId(),
        message: 'Registration successful. Please verify your email.'
      };

    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<boolean> {
    // Token verification would be handled by a separate service
    // This is just the orchestration
    const personAuthId = await this.validateEmailToken(token);
    if (!personAuthId) return false;

    const personAuth = await this.personAuthRepo.findById(personAuthId);
    if (!personAuth) return false;

    personAuth.verifyEmail();
    await this.personAuthRepo.save(personAuth);
    await this.publishEvents(personAuth);

    return true;
  }

  private async validateEmailToken(token: string): Promise<string | null> {
    // Implementation would use a token service
    // Placeholder for now
    return null;
  }

  private async publishEvents(aggregate: PersonAuth): Promise<void> {
    const events = aggregate.getUncommittedEvents();
    for (const event of events) {
      await this.eventBus.publish(event);
    }
    aggregate.markEventsAsCommitted();
  }
}